import { fieldLabel, isBooleanField, isPrimaryKey, isStructured, type Field } from "./schema-field";
import {
  looksNumericForChart,
  looksTemporalForChart,
} from "./chart-field-guess";

/**
 * A chart the collection page can mount without a prior /ui/generate result.
 *
 * Same fields ChartCard reads from a persisted ChartDto, plus an optional
 * measure. Ids are stable for a given collection+axis so React state and the
 * unplottable report map stay coherent across re-renders.
 */
export type SuggestedChart = {
  id: string;
  name: string;
  title: string;
  description?: string;
  type: "PIE" | "BAR" | "LINE";
  collections: string[];
  groupBy: string;
  bucket?: "month";
  measure?: string;
};

/** Columns that read as a small fixed set (status, product, type, …). */
const TAXONOMY =
  /^(status|state|stage|phase|priority|severity|severity_level|type|kind|category|role|product|channel|source|tier|plan|level|grade|region|country|currency|payment|method)$/i;
/** Prefer a status-like pie before Product when both are taxonomy columns. */
const STATUS_LIKE = /^(status|state|stage|phase)$/i;

/** Free-text / identity names that must not become a chart axis. */
const NEVER_CATEGORY =
  /^(id|uuid|guid|pk|name|title|label|description|desc|note|notes|comment|comments|bio|body|content|text|message|email|e[_-]?mail|phone|mobile|url|uri|link|href|path|slug|token|hash|password|passwd|secret|key|address|street|city|zip|postal|code|customer|client|user|owner)$|_id$|_(name|title|label|email|phone|address)$|^(customer|client|user|owner)_/i;

/** Keep in sync with chart-field-guess: skip category compounds like price_tier. */
const MEASURE_HINTS =
  /(amount|total|sum|budget|cost|price|value|revenue|spend|score|qty|quantity|count|hours|duration)(?![\w-]*(?:tier|level|grade|type|kind|category|class|status)\b)/i;
/** Prefer a Total/Amount column over Unit Price or Quantity. */
const MEASURE_RANK = [
  /^(amount|total|sum|budget|revenue)$/i,
  /(amount|total|sum|budget|revenue)/i,
  /(cost|price|value|spend)/i,
  MEASURE_HINTS,
];

const AUDIT_TEMPORAL = /^(created|updated|deleted|modified)_?(at|on|date|time)$/i;

const normalize = (name: string): string =>
  name.trim().toLowerCase().replace(/[\s-]+/g, "_");

const isTaxonomy = (field: Field): boolean => TAXONOMY.test(normalize(field.name));

const isNeverCategory = (field: Field): boolean =>
  NEVER_CATEGORY.test(normalize(field.name));

const pickMeasure = (fields: Field[], used: Set<string>): Field | undefined => {
  const candidates = fields.filter(
    (field) =>
      !used.has(field.name) &&
      !isPrimaryKey(field) &&
      looksNumericForChart(field),
  );
  for (const pattern of MEASURE_RANK) {
    const match = candidates.find((field) => pattern.test(normalize(field.name)) || pattern.test(field.name));
    if (match) return match;
  }
  return candidates[0];
};

const categoricalCandidates = (fields: Field[]): Field[] => {
  const usable = fields.filter(
    (field) =>
      !isPrimaryKey(field) &&
      !looksTemporalForChart(field) &&
      !looksNumericForChart(field) &&
      !isStructured(field) &&
      !isNeverCategory(field),
  );

  const enums = usable.filter((field) => !!(field as Field).isEnum);
  const taxonomy = usable
    .filter(isTaxonomy)
    .sort((a, b) => {
      // Sample Orders CSV lists Product before Status; still want Status first.
      const aStatus = STATUS_LIKE.test(normalize(a.name)) ? 0 : 1;
      const bStatus = STATUS_LIKE.test(normalize(b.name)) ? 0 : 1;
      return aStatus - bStatus;
    });
  const booleans = usable.filter(isBooleanField);
  // Enums and named taxonomies first: Sheets analyse often leaves Status as a
  // bare STRING with isEnum=false, and the API generator then emits only a
  // month line. The Sample Orders sheet is the live case.
  const ranked = [
    ...enums.filter(isTaxonomy),
    ...enums,
    ...taxonomy,
    ...booleans,
    ...usable.filter(
      (field) => !enums.includes(field) && !taxonomy.includes(field) && !booleans.includes(field),
    ),
  ];

  const seen = new Set<string>();
  const out: Field[] = [];
  for (const field of ranked) {
    if (seen.has(field.name)) continue;
    seen.add(field.name);
    out.push(field);
  }
  return out;
};

const temporalCandidates = (fields: Field[]): Field[] => {
  const temporals = fields.filter(
    (field) => !isPrimaryKey(field) && looksTemporalForChart(field),
  );
  const audit = temporals.filter((field) => AUDIT_TEMPORAL.test(field.name));
  const rest = temporals.filter((field) => !AUDIT_TEMPORAL.test(field.name));
  // Prefer an audit timestamp for "added over time", then a business date.
  return [...audit, ...rest];
};

/**
 * Up to three charts from the collection schema alone.
 *
 * Used when /ui/generate left no charts (prod has no LLM key) or when every
 * stored chart came back unplottable (a single-month line on the Sample sheet).
 * Mirrors the API's chartsFromCollection ranking: taxonomy pie, month line,
 * second categorical. Each column is used at most once.
 */
export function suggestChartsFromSchema(
  collectionName: string | undefined,
  fields: Field[] | undefined,
): SuggestedChart[] {
  if (!collectionName || !fields?.length) return [];

  const used = new Set<string>();
  const charts: SuggestedChart[] = [];
  const entity = fieldLabel({ name: collectionName } as Field) || collectionName;

  const push = (chart: Omit<SuggestedChart, "id" | "collections">) => {
    if (used.has(chart.groupBy)) return;
    if (charts.length >= 3) return;
    used.add(chart.groupBy);
    charts.push({
      ...chart,
      id: `suggest:${collectionName}:${chart.groupBy}:${chart.type}`,
      collections: [collectionName],
    });
  };

  const categories = categoricalCandidates(fields);
  const firstCategory = categories[0];
  if (firstCategory) {
    const taxonomyPie = isTaxonomy(firstCategory) || !!(firstCategory as Field).isEnum;
    push({
      name: `${entity} by ${fieldLabel(firstCategory)}`,
      title: `${entity} by ${fieldLabel(firstCategory)}`,
      type: taxonomyPie ? "PIE" : "BAR",
      groupBy: firstCategory.name,
    });
  }

  // Prefer a summed measure by a second category (Total by Product) before a
  // short month series. Sample Orders has five products with equal counts but
  // different totals; that bar is more useful than a two-month line.
  // Measures may be reused across charts; `used` only tracks groupBy columns.
  const second = categories.find((field) => !used.has(field.name));
  const measureForCategory = pickMeasure(fields, used);
  if (second && measureForCategory) {
    push({
      name: `${fieldLabel(measureForCategory)} by ${fieldLabel(second)}`,
      title: `${fieldLabel(measureForCategory)} by ${fieldLabel(second)}`,
      type: "BAR",
      groupBy: second.name,
      measure: measureForCategory.name,
    });
  } else if (second) {
    push({
      name: `${entity} by ${fieldLabel(second)}`,
      title: `${entity} by ${fieldLabel(second)}`,
      type: "BAR",
      groupBy: second.name,
    });
  }

  const temporal = temporalCandidates(fields)[0];
  if (temporal) {
    // Audit timestamps stay a row count. A business date SUMs Total (or the
    // next measure) so Sample Orders gets "Total by month of Date".
    const measure = AUDIT_TEMPORAL.test(temporal.name)
      ? undefined
      : pickMeasure(fields, used);
    push({
      name: AUDIT_TEMPORAL.test(temporal.name)
        ? `${entity} added over time`
        : measure
          ? `${fieldLabel(measure)} by month of ${fieldLabel(temporal)}`
          : `${entity} by month of ${fieldLabel(temporal)}`,
      title: AUDIT_TEMPORAL.test(temporal.name)
        ? `${entity} added over time`
        : measure
          ? `${fieldLabel(measure)} by month of ${fieldLabel(temporal)}`
          : `${entity} by month of ${fieldLabel(temporal)}`,
      type: "LINE",
      groupBy: temporal.name,
      bucket: "month",
      measure: measure?.name,
    });
  }

  return charts;
}
